import React from "react"; // 👈 required if ESLint still enforces react-in-jsx-scope
import { useSelector } from "react-redux";
import { componentKey } from "../../patientSlice";
import {
  getConditionDisplayName,
  getFrequencyDisplayName,
  getVitalDisplayName,
} from "../../../../utils/getCustomiseDisplayName";
import { BundleOrderData } from "./DispositionDialog";
import Table from "../../../../component-lib/Organisms/table/EventEHRTable";

const BundleOrderDisplay: React.FC = () => {
  const data = useSelector(
    (state: any) => state[componentKey].bundleOrderData
  ) as BundleOrderData;

  if (!data) return <></>;

  const rxConditions = data.conditions.filter(
    (c) => !c.type || c.type === "Rx Order"
  );
  const nonRxConditions = data.conditions.filter(
    (c) => c.type && c.type !== "Rx Order"
  );

  // Rx Order table — mirrors the visit workflow's Rx order table
  const rxColumns = [
    {
      field: "condition",
      label: <div>Condition</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">
          {getConditionDisplayName(row?.conditionName || "")}
        </p>
      ),
    },
    {
      field: "medication",
      label: <div>Medication</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{row?.medicationName || "-"}</p>
      ),
    },
    {
      field: "frequency",
      label: <div>Frequency</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">
          {getFrequencyDisplayName(row?.frequency || "")}
        </p>
      ),
    },
    {
      field: "quantity",
      label: <div>Quantity</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{row?.quantity ?? "-"}</p>
      ),
    },
  ];

  // Non Rx Order table — Order Type / Item / Note
  const nonRxColumns = [
    {
      field: "orderType",
      label: <div>Order Type</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{row?.type?.split("_")?.join(" ") || "-"}</p>
      ),
    },
    {
      field: "item",
      label: <div>Item</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{row?.name || "-"}</p>
      ),
    },
    {
      field: "note",
      label: <div>Note</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{row?.note || "-"}</p>
      ),
    },
  ];

  const vitalColumns = [
    {
      field: "vital",
      label: <div>Vital</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">{getVitalDisplayName(row?.vitalName)}</p>
      ),
    },
    {
      field: "frequency",
      label: <div>Frequency</div>,
      renderLogic: (row: any) => (
        <p className="font-normal">
          {getFrequencyDisplayName(row?.frequency || "")}
        </p>
      ),
    },
  ];

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Bundle Order</h3>

      {/* RX ORDER TABLE */}
      {rxConditions.length > 0 && (
        <div className="mb-6">
          <h4 className="text-base font-medium text-gray-700 mb-3">Rx Order</h4>
          <Table columns={rxColumns} rows={rxConditions} />
        </div>
      )}

      {/* NON RX ORDER TABLE */}
      {nonRxConditions.length > 0 && (
        <div className="mb-6">
          <h4 className="text-base font-medium text-gray-700 mb-3">
            Non Rx Order
          </h4>
          <Table columns={nonRxColumns} rows={nonRxConditions} />
        </div>
      )}

      {/* VITALS TABLE */}
      {data.vitals.length > 0 && (
        <div className="mb-6">
          <h4 className="text-base font-medium text-gray-700 mb-3">Vitals</h4>
          <Table columns={vitalColumns} rows={data.vitals} />
        </div>
      )}
    </div>
  );
};

export default BundleOrderDisplay;
